import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  executeQuery,
  executeMutation,
  plateImageGenerator,
} from "../../graphql";
import {
  getPlateStyles,
  getBrowseStyles,
  getVariantWithColourThemeAndPlatesByProductId,
  validatePlateNumber,
} from "../../graphql/queries";
import {
  preparePlateImageInput,
  getPlateImageDataUrl,
} from "../../utils/plateImageHelper";
import {
  namedColoursForCards,
  matchesColourFilter,
} from "../../utils/colourFilter";
import Wizard from "../../components/Wizard";
import ProductFilter from "../../components/ProductFilter";
import ProductList from "../../components/ProductList";
import LoadingPanel from "../../components/LoadingPanel";
import { ProductGridSkeleton } from "../../components/ProductCardSkeleton";
import { usePageContent } from "../../hooks/usePageContent";
import BuyNowComboContent from "../../components/BuyNowComboContent";
import {
  PRODUCT_TYPE,
  buildPlateItem,
  prodSizeName,
  plateItemFromOrder,
  trackSelectAvailability,
  trackSelectItem,
  trackAddToCart,
} from "../../utils/analytics";
import styles from "./index.module.css";

const CreateStylesPage = () => {
  const navigate = useNavigate();
  const dialogRef = useRef(null);
  const endCtaRef = useRef(null);
  const modalHistoryPushed = useRef(false);
  const pendingHistoryBack = useRef(false);

  const { find } = usePageContent('create-styles');
  const wizardContent = find('wizard')        || {};
  const c             = find('styleSelector') || {};

  const { find: findDetailLabels } = usePageContent('browse-styles-detail');
  const comboLabels = findDetailLabels('styleDetail') || {};

  const urlParams = new URLSearchParams(window.location.search);
  const session = (() => { try { return JSON.parse(sessionStorage.getItem('plateOrderData') || '{}'); } catch { return {}; } })();
  const combination = urlParams.get('combination') || session.combination || "";
  const vehicleType = urlParams.get('vehicleType') || session.vehicleType || "car";
  const vehicleTypeId = vehicleType === "car" ? 1 : 2;
  // If the URL carries a different combination than what's stored, this is a new journey —
  // don't carry over the previous journey's product selection.
  const isNewCombination = !!(urlParams.get('combination') && session.combination && urlParams.get('combination') !== session.combination);
  const sessionProductName = urlParams.get('productName') || (isNewCombination ? '' : session.productName) || "";
  const sessionVariantColour = urlParams.get('colour') || (isNewCombination ? '' : session.variantColour) || "";

  const comboBackHref = `/create/check-combination?combination=${encodeURIComponent(combination)}&vehicleType=${encodeURIComponent(vehicleType)}`;
  const wizardSteps = (wizardContent.steps || [
    { title: 'Check combination' },
    { title: 'Select a style' },
    { title: 'Confirm personal details', href: '/create/personal-details' },
    { title: 'Checkout', href: '/create/checkout' },
  ]).map((step, i) => i === 0 ? { ...step, href: comboBackHref } : step);

  const getSizeLabel = (code, fallback, product = selectedProduct) => {
    const single = vehicleType !== 'car';
    const isEuro = product?.ProductName?.toLowerCase().includes('euro');
    if (code === 'ST') {
      if (isEuro) return c.standardEuroSizeLabel || 'Standard Euro';
      return single ? (c.standardSizeLabelSingle || '1 Standard') : (c.standardSizeLabel || '2 Standard');
    }
    if (code === 'SL') {
      if (isEuro) return c.miniEuroSizeLabel || 'Mini Euro';
      return single ? (c.slimlineSizeLabelSingle || '1 Slimline') : (c.slimlineSizeLabel || '2 Slimline');
    }
    if (code === 'OOE') return isEuro ? (c.combinedEuroSizeLabel || '1 Euro & 1 Mini Euro') : (c.combinedSizeLabel || '1 Standard & 1 Slimline');
    return fallback || code;
  };

  const formatDimensions = (variant) => {
    const w = variant?.SizeInfo?.PlateWidth_mm;
    const h = variant?.SizeInfo?.PlateHeight_mm;
    return w && h ? `${w}mm (w) ${h}mm (h)` : '';
  };

  const [products, setProducts] = useState([]);
  const [browseVariantMap, setBrowseVariantMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const UNAVAILABLE_SLUG_MAP = { 'Euro Premium': 'euro', 'Euro Standard': 'euro' };
  const unavailableProductSlug = selectedProduct?.IsAvailable !== true
    ? (UNAVAILABLE_SLUG_MAP[selectedProduct?.ProductName] || selectedProduct?.ProductName?.toLowerCase().replace(/\s+/g, '-') || '_none_')
    : '_none_';
  const { find: findUnavailableContent } = usePageContent(`browse-styles/${unavailableProductSlug}`);
  const unavailableSpecs = findUnavailableContent('plateSpecs') || {};
  const unavailableBuyNowModal = findUnavailableContent('buyNowModal') || {};

  const [filterCategory, setFilterCategory] = useState([]);
  const [filterPrice, setFilterPrice] = useState([]);
  const [filterColour, setFilterColour] = useState([]);
  const [filterSize, setFilterSize] = useState([]);
  const [sortCriteria, setSortCriteria] = useState("popular");
  const [displayCount, setDisplayCount] = useState(150);
  const [selectedColourScheme, setSelectedColourScheme] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState("");
  const [selectedSize, setSelectedSize] = useState(null);
  const [allVariants, setAllVariants] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [generatedPlateImage, setGeneratedPlateImage] = useState(null);
  const [plateSTImage, setPlateSTImage] = useState(null);
  const [plateSLImage, setPlateSLImage] = useState(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [productImages, setProductImages] = useState({});
  const [availabilityError, setAvailabilityError] = useState("");
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [isEndCtaVisible, setIsEndCtaVisible] = useState(false);
  // Ensure combination is in session so Step 1 wizard nav restores it.
  // If the URL specifies a different combination than what's stored, start fresh — don't
  // carry over product selection from a previously completed journey.
  useEffect(() => {
    if (combination) {
      const current = (() => { try { return JSON.parse(sessionStorage.getItem('plateOrderData') || '{}'); } catch { return {}; } })();
      const urlCombo = urlParams.get('combination');
      const isNewJourney = !!(urlCombo && current.combination && urlCombo !== current.combination);
      const updated = isNewJourney ? { combination, vehicleType } : { ...current, combination, vehicleType };
      sessionStorage.setItem('plateOrderData', JSON.stringify(updated));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // GA: select_availability — once per page load when arriving with a combination in the URL
  // (prod: filteredStyles.js). Ref guard keeps StrictMode's double effect run from duplicating it.
  const availabilityTrackedRef = useRef(false);
  useEffect(() => {
    if (availabilityTrackedRef.current) return;
    availabilityTrackedRef.current = true;
    if (urlParams.get('combination')) trackSelectAvailability(combination);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchStyles = async () => {
      if (!combination) {
        navigate("/");
        return;
      }

      setLoading(true);
      try {
        const result = await executeQuery(getPlateStyles, {
          CombinationType: combination,
          VehicleTypeId: vehicleTypeId,
          SalesChannel: 1,
        });

        if (result.getProductsWithVariantsByCombination) {
          setProducts(result.getProductsWithVariantsByCombination);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchStyles();
  }, [combination, vehicleTypeId, navigate]);

  useEffect(() => {
    executeQuery(getBrowseStyles, {})
      .then((result) => {
        const browseProducts = result?.getBrowseStyleProducts || [];
        const map = {};
        for (const p of browseProducts) {
          if (p.ProductId && p.VariantsInBrowseStyle?.length) {
            map[p.ProductId] = p.VariantsInBrowseStyle;
          }
        }
        setBrowseVariantMap(map);
      })
      .catch(() => {});
  }, []);


  useEffect(() => {
    if (!products || products.length === 0) return;
    const cards = expandToVariantCards(products);
    const imagesMap = {};
    for (const card of cards) {
      const isLucky = card.ProductName?.toLowerCase().includes('lucky');
      const isMotorcycle = card.ProductName?.toLowerCase().includes('motorcycle') && card.ProductName?.toLowerCase().includes('custom');
      let fileName;
      if (isLucky) {
        const safeColour = (card.VariantsInProduct[0]?.ColourInfo?.DisplayName || card._variantColour || '').replace(/\s+/g, '_');
        const safeTheme = (card._variantTheme || '').replace(/\s+/g, '_');
        fileName = `Lucky_${safeColour}_${safeTheme}.png`;
      } else if (card._variantTheme && card._variantTheme !== card._variantColour) {
        const safeName = card.ProductName.replace(/\s+/g, '_');
        const safeColour = (card._variantColour || '').replace(/\s+/g, '_');
        const safeTheme = (card._variantTheme || '').replace(/\s+/g, '_');
        fileName = `${safeName}_${safeColour}_${safeTheme}.png`;
      } else if (isMotorcycle) {
        const safeColour = (card._variantColour || '').replace(/\s+/g, '_');
        fileName = `Motorcycle_Custom_${safeColour}.png`;
      } else {
        const safeName = card.ProductName.replace(/\s+/g, '_');
        const safeColour = (card._variantColour || '').replace(/\s+/g, '_');
        fileName = safeColour ? `${safeName}_${safeColour}.png` : `${safeName}.png`;
      }
      imagesMap[card.ProductId] = `/sampleimages/${fileName}`;
    }
    setProductImages(imagesMap);
  // Unavailable products have no VariantsInProduct, so expandToVariantCards falls
  // back to browseVariantMap (fetched separately). Re-run when it arrives so
  // unavailable cards get per-variant images instead of the generic SampleImageId.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, browseVariantMap]);

  // Open dialog after React renders the new selectedProduct;
  // push a history entry so the browser back button closes the modal
  // instead of navigating away from the page.
  useEffect(() => {
    if (selectedProduct && dialogRef.current && !dialogRef.current.open) {
      window.history.pushState({ modal: true }, '');
      modalHistoryPushed.current = true;
      dialogRef.current.showModal();
      // Native <dialog> auto-focuses the first focusable element (the close/back
      // button), which then shows its highlighted :focus state and draws the eye
      // away from progressing. Move initial focus to the title instead — screen
      // readers still announce the modal, and the back button stays neutral.
      dialogRef.current.querySelector('.modal-selection__title')?.focus();
    }
  }, [selectedProduct]);

  // Close modal when the browser back button is pressed (popstate).
  // Ignore popstate events triggered by our own programmatic history.back()
  // inside handleDialogClose to avoid closing a newly-opened modal.
  useEffect(() => {
    const handlePopState = () => {
      if (pendingHistoryBack.current) {
        pendingHistoryBack.current = false;
        return;
      }
      if (dialogRef.current?.open) {
        modalHistoryPushed.current = false;
        dialogRef.current.close();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Restore previous selection when navigating back from downstream pages
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (autoSelectedRef.current || !sessionProductName || loading || products.length === 0) return;
    autoSelectedRef.current = true;
    const allCards = expandToVariantCards(products.filter(p => p.IsAvailable === true));
    const card = (sessionVariantColour
      ? allCards.find(c => c.ProductName === sessionProductName && c._variantColour === sessionVariantColour)
      : null) || allCards.find(c => c.ProductName === sessionProductName);
    // isAuto: restoring a previous selection is not a user click — no select_item.
    if (card) handleProductClick(card, { preventDefault: () => {} }, { isAuto: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, loading]);

  const allPriceRanges = ["501 - 1000", "1001 - 9000"];

  const normalizeHex = (color) => {
    if (!color) return "ffffff";
    return String(color).replace(/^#/, "");
  };

  // Expand each product's colour/theme variants into individual flat cards
  const expandToVariantCards = (productList) => {
    const cards = [];
    for (const product of productList) {
      const rawVariants = product.VariantsInProduct?.length
        ? product.VariantsInProduct
        : (browseVariantMap[product.ProductId] || []);

      const isLucky = product.ProductName?.toLowerCase().includes("lucky");
      const isThemeBased = rawVariants.some((v) => v.ThemeInfo?.Theme);
      const isMultiColourTheme = isThemeBased && !isLucky && (() => {
        const themeColours = {};
        for (const v of rawVariants) {
          const theme = v.ThemeInfo?.Theme || '';
          const colour = v.ColourInfo?.DisplayName || v.ColourInfo?.Colour || '';
          if (!themeColours[theme]) themeColours[theme] = new Set();
          if (colour) themeColours[theme].add(colour);
        }
        return Object.values(themeColours).some((colours) => colours.size > 1);
      })();
      const getGroupKey = (v) => {
        if (isLucky || isMultiColourTheme) {
          const colour =
            v.ColourInfo?.DisplayName || v.ColourInfo?.Colour || "";
          const theme = v.ThemeInfo?.Theme || "";
          return `${colour}_${theme}`;
        }
        return isThemeBased
          ? v.ThemeInfo?.Theme
          : v.ColourInfo?.DisplayName || v.ColourInfo?.Colour;
      };

      const variants = [...rawVariants].sort((a, b) =>
        (getGroupKey(a) || "").localeCompare(getGroupKey(b) || "")
      );

      const groups = {};
      const ungroupedCosts = [];
      for (const variant of variants) {
        const key = getGroupKey(variant);
        const ci = variant.CostInfoForCreate;
        const cost = (() => {
          const raw = Array.isArray(ci)
            ? ci.map(e => parseFloat(e?.Cost)).filter(x => !isNaN(x) && x > 0).sort((a,b) => a-b)[0]
            : parseFloat(ci?.Cost);
          return !isNaN(raw) && raw > 0 ? raw : null;
        })();
        if (!key) {
          if (cost != null && cost > 0) ungroupedCosts.push(cost);
          continue;
        }
        if (!groups[key]) {
          groups[key] = { variant, minCost: null, maxCost: null, sizeCodes: [] };
        }
        if (cost != null && cost > 0) {
          if (groups[key].minCost === null || cost < groups[key].minCost) {
            groups[key].minCost = cost;
          }
          if (groups[key].maxCost === null || cost > groups[key].maxCost) {
            groups[key].maxCost = cost;
          }
        }
        const sizeCode = variant.SizeInfo?.SizeCode;
        if (sizeCode && !groups[key].sizeCodes.includes(sizeCode)) {
          groups[key].sizeCodes.push(sizeCode);
        }
      }
      // Colourless variants (no group key) contribute to every group's min/max
      if (ungroupedCosts.length > 0) {
        for (const g of Object.values(groups)) {
          for (const cost of ungroupedCosts) {
            if (g.minCost === null || cost < g.minCost) g.minCost = cost;
            if (g.maxCost === null || cost > g.maxCost) g.maxCost = cost;
          }
        }
      }

      for (const [key, { variant, minCost, maxCost, sizeCodes }] of Object.entries(
        groups
      )) {
        const hasST = sizeCodes.includes("ST");
        const hasSL = sizeCodes.includes("SL");
        const allSizeCodes = hasST && hasSL ? [...sizeCodes, "OOE"] : sizeCodes;
        cards.push({
          ProductId: `${product.ProductId}_${key}`,
          _originalProductId: product.ProductId,
          _variantColour: (isLucky || isMultiColourTheme)
            ? variant.ColourInfo?.DisplayName ||
              variant.ColourInfo?.Colour ||
              key
            : key,
          _variantTheme: (isLucky || isMultiColourTheme)
            ? variant.ThemeInfo?.Theme || null
            : isThemeBased
            ? key
            : null,
          _hexOuter: variant.ColourInfo?.ColourSelectorHexOuter
            ? String(variant.ColourInfo.ColourSelectorHexOuter).replace(/^#/, '')
            : '',
          _sizeCodes: allSizeCodes,
          _showFrom: minCost !== null && maxCost !== null && maxCost > minCost,
          ProductName: product.ProductName,
          SampleImageId: product.SampleImageId,
          ListPrice: minCost !== null ? minCost : product.ListPrice,
          IsAvailable: product.IsAvailable,
          _categoryName: product.CategoryDesc?.CategoryName || "",
          CategoryDesc: product.CategoryDesc,
          VariantsInProduct: [variant],
        });
      }

      if (rawVariants.length === 0) {
        cards.push({
          ProductId: product.ProductId,
          _originalProductId: product.ProductId,
          _variantColour: null,
          _variantTheme: null,
          ProductName: product.ProductName,
          SampleImageId: product.SampleImageId,
          ListPrice: product.ListPrice,
          IsAvailable: product.IsAvailable,
          _categoryName: product.CategoryDesc?.CategoryName || "",
          CategoryDesc: product.CategoryDesc,
          VariantsInProduct: [],
        });
      }
    }

    // Attach _otherColours: sibling colour swatches for the same theme.
    // For variants sharing the same background hex (e.g. two white-bg Japanese plates),
    // sort alphabetically — first keeps background hex, others use the "with X" font word.
    const themeHexGroups = {};
    for (const card of cards) {
      if (!card._variantTheme) continue;
      if (!themeHexGroups[card._variantTheme]) themeHexGroups[card._variantTheme] = {};
      const hk = card._hexOuter || '';
      if (!themeHexGroups[card._variantTheme][hk]) themeHexGroups[card._variantTheme][hk] = [];
      themeHexGroups[card._variantTheme][hk].push(card);
    }
    const swatchMap = new Map();
    for (const hexGroups of Object.values(themeHexGroups)) {
      for (const group of Object.values(hexGroups)) {
        const sorted = [...group].sort((a, b) => (a._variantColour || '').localeCompare(b._variantColour || ''));
        sorted.forEach((card, idx) => {
          if (idx === 0) {
            swatchMap.set(card.ProductId, { hex: card._hexOuter || '', swatchColour: '' });
          } else {
            const m = card._variantColour?.match(/with\s+(\w+)/i);
            swatchMap.set(card.ProductId, { hex: '', swatchColour: (m ? m[1] : card._variantColour || '').toLowerCase() });
          }
        });
      }
    }
    const themeColourMap = {};
    for (const card of cards) {
      if (!card._variantTheme) continue;
      if (!themeColourMap[card._variantTheme]) themeColourMap[card._variantTheme] = [];
      const si = swatchMap.get(card.ProductId) || { hex: card._hexOuter || '', swatchColour: '' };
      themeColourMap[card._variantTheme].push({ hex: si.hex, swatchColour: si.swatchColour, colour: card._variantColour });
    }
    for (const card of cards) {
      if (card._variantTheme && themeColourMap[card._variantTheme]) {
        card._otherColours = themeColourMap[card._variantTheme]
          .filter((c) => c.colour !== card._variantColour)
          .slice(0, 5);
      }
    }

    return cards;
  };

  const allCategories = [
    ...new Set(
      products.map((p) => p.CategoryDesc?.CategoryName).filter(Boolean)
    ),
  ].sort();

  const availableProducts = products.filter((p) => p.IsAvailable === true && (p.VariantsInProduct?.length ?? 0) > 0);
  const unavailableProducts = products.filter((p) => p.IsAvailable !== true || (p.VariantsInProduct?.length ?? 0) === 0);

  const availableCards = expandToVariantCards(availableProducts);

  const allColours = namedColoursForCards(availableCards);

  const allSizes = (() => {
    const seen = new Set();
    for (const card of availableCards) {
      for (const code of card._sizeCodes || []) seen.add(code);
    }
    const order = { OOE: 0, ST: 1, SL: 2 };
    return [...seen]
      .sort((a, b) => (order[a] ?? 99) - (order[b] ?? 99))
      .map((code) => ({ code }));
  })();

  const filterCard = (card) => {
    if (
      filterCategory.length > 0 &&
      !filterCategory.includes(card._categoryName)
    )
      return false;
    if (filterPrice.length > 0) {
      const price = card.ListPrice;
      const inRange = filterPrice.some((range) => {
        if (range === "501 - 1000") return price > 500 && price <= 1000;
        if (range === "1001 - 9000") return price > 1000 && price <= 9000;
        return false;
      });
      if (!inRange) return false;
    }
    if (
      filterColour.length > 0 &&
      !matchesColourFilter(card, filterColour)
    )
      return false;
    if (
      filterSize.length > 0 &&
      !filterSize.some((s) => (card._sizeCodes || []).includes(s))
    )
      return false;
    return true;
  };

  const sortCards = (cards) =>
    [...cards].sort((a, b) => {
      if (sortCriteria === "price-low") return a.ListPrice - b.ListPrice;
      if (sortCriteria === "price-high") return b.ListPrice - a.ListPrice;
      if (sortCriteria === 'name-asc') return (a.ProductName || '').localeCompare(b.ProductName || '');
      if (sortCriteria === 'name-desc') return (b.ProductName || '').localeCompare(a.ProductName || '');
      return 0;
    });

  const sortedAvailableProducts = sortCards(availableCards.filter(filterCard));
  const activeFilterCount = filterCategory.length + filterPrice.length + filterColour.length + filterSize.length;
  const sortedUnavailableProducts = sortCards(
    expandToVariantCards(unavailableProducts).filter(filterCard)
  );

  const displayedUnavailableProducts = sortedUnavailableProducts.slice(
    0,
    displayCount
  );

  const closeDialog = () => {
    dialogRef.current?.close();
  };

  const handleDialogClose = () => {
    // If the modal was closed by the close button / backdrop / Escape (not by
    // browser-back), remove the history entry we pushed when opening.
    // Flag pendingHistoryBack so our popstate handler ignores the resulting event.
    if (modalHistoryPushed.current) {
      modalHistoryPushed.current = false;
      pendingHistoryBack.current = true;
      window.history.back();
    }
    setSelectedProduct(null);
    setAllVariants([]);
    setSelectedColourScheme(null);
    setSelectedTheme("");
    setSelectedSize(null);
    setGeneratedPlateImage(null);
    setPlateSTImage(null);
    setPlateSLImage(null);
    setAvailabilityError("");
  };

  const handleProductClick = async (product, e, { isAuto = false } = {}) => {
    e.preventDefault();
    setSelectedProduct(product);

    if (product.IsAvailable === true) {
      setLoadingVariants(true);

      try {
        const productId = product._originalProductId || product.ProductId;
        const result = await executeQuery(
          getVariantWithColourThemeAndPlatesByProductId,
          {
            ProductId: productId,
            CombinationType: combination,
            SalesChannel: 1,
            IsDisplayBrowse: false,
          }
        );

        if (result.getVariantByProductId) {
          const variants = result.getVariantByProductId;
          setAllVariants(variants);

          const targetColour = product._variantColour;
          const targetTheme = product._variantTheme;
          const matchingVariant = (targetTheme && targetColour)
            ? (variants.find((v) =>
                v.ThemeInfo?.Theme === targetTheme &&
                (v.ColourInfo?.DisplayName === targetColour || v.ColourInfo?.Colour === targetColour))
              || variants.find((v) => v.ThemeInfo?.Theme === targetTheme))
            : targetTheme
            ? variants.find((v) => v.ThemeInfo?.Theme === targetTheme)
            : targetColour
            ? variants.find(
                (v) =>
                  v.ColourInfo?.DisplayName === targetColour ||
                  v.ColourInfo?.Colour === targetColour
              )
            : variants.find((v) => v.ColourInfo);
          const firstVariant =
            matchingVariant || variants.find((v) => v.ColourInfo);

          if (firstVariant) {
            const isThemeBased = variants.some(v => v.ThemeInfo?.Theme);
            const isLucky = product.ProductName?.toLowerCase().includes('lucky');
            const isMultiColourThemeLocal = isThemeBased && !isLucky && (() => {
              const themeColours = {};
              for (const v of variants) {
                const theme = v.ThemeInfo?.Theme || '';
                const colour = v.ColourInfo?.DisplayName || v.ColourInfo?.Colour || '';
                if (!themeColours[theme]) themeColours[theme] = new Set();
                if (colour) themeColours[theme].add(colour);
              }
              return Object.values(themeColours).some(colours => colours.size > 1);
            })();
            const colourKey = firstVariant.ColourInfo
              ? (isThemeBased && !isLucky && !isMultiColourThemeLocal
                  ? '_theme_'
                  : `${firstVariant.ColourInfo.DisplayName || ''}_${firstVariant.ColourInfo.ColourSelectorHexOuter}_${firstVariant.ColourInfo.ColourSelectorHexFont}`)
              : String(firstVariant.VariantId);
            setSelectedColourScheme(colourKey);
            if (firstVariant.ThemeInfo?.Theme)
              setSelectedTheme(firstVariant.ThemeInfo.Theme);
            const hasRealOOE = variants.some((v) => v.IsOOE === true);
            const defaultSize = hasRealOOE
              ? "OOE"
              : firstVariant.SizeInfo?.SizeCode || null;
            setSelectedSize(defaultSize);

            // GA: select_item — the variant/size the modal opens with (prod: productStyleForm.js).
            if (!isAuto) {
              const shownVariant = defaultSize === "OOE"
                ? (variants.find((v) =>
                    v.IsOOE === true &&
                    (v.ThemeInfo?.Theme || '') === (firstVariant.ThemeInfo?.Theme || '') &&
                    (v.ColourInfo?.DisplayName || v.ColourInfo?.Colour) === (firstVariant.ColourInfo?.DisplayName || firstVariant.ColourInfo?.Colour))
                  || firstVariant)
                : firstVariant;
              trackSelectItem(buildPlateItem({
                productId: productId,
                productName: product.ProductName,
                productType: PRODUCT_TYPE.CREATE,
                vehicleType,
                variantKey: shownVariant.VariantName || shownVariant.VariantId,
                colour: shownVariant.ColourInfo?.DisplayName || shownVariant.ColourInfo?.Colour || product._variantColour,
                sizeName: prodSizeName(defaultSize, shownVariant.SizeInfo?.SizeDescription, product.ProductName),
                combination,
                price: shownVariant.CostInfoForCreate?.Cost || product.ListPrice,
              }, false));
            }
          }
        }
      } catch (error) {
        console.error("Error fetching variants:", error);
      } finally {
        setLoadingVariants(false);
      }
    }
  };

  const handleGetThisPlate = async (e) => {
    e.preventDefault();
    const selectedVariant = getSelectedVariant();

    if (!selectedVariant) {
      return;
    }

    setAvailabilityError("");
    setCheckingAvailability(true);
    try {
      const result = await executeQuery(validatePlateNumber, {
        PlateCombo: combination,
        VehicleType: vehicleTypeId,
        SalesChannel: 1,
      });
      if (result?.getPlateRuleEngineStatus?.Status !== 1) {
        setAvailabilityError(
          c.availabilityErrorText || "This plate combination is no longer available. Please go back and try a different combination."
        );
        return;
      }
    } catch {
      setAvailabilityError(
        c.availabilityVerifyErrorText || "Unable to verify plate availability. Please try again."
      );
      return;
    } finally {
      setCheckingAvailability(false);
    }

    const isOOE =
      selectedSize &&
      (selectedSize.toLowerCase() === "ooe" || selectedSize === "OOE");

    const orderData = {
      combination,
      vehicleType,
      productId: selectedProduct.ProductId,
      analyticsProductId: selectedProduct._originalProductId || selectedProduct.ProductId,
      productName: selectedProduct.ProductName,
      variantColour: selectedVariant.ColourInfo?.DisplayName || selectedVariant.ColourInfo?.Colour || selectedProduct._variantColour || '',
      categoryName: selectedProduct.CategoryDesc?.CategoryName,
      variantId: selectedVariant.VariantId,
      variantName: selectedVariant.VariantName,
      colorName: selectedVariant.ColourInfo?.Colour,
      colorHexForeground: selectedVariant.ColourInfo?.ColourSelectorHexFont,
      colorHexBackground: selectedVariant.ColourInfo?.ColourSelectorHexOuter,
      theme: selectedTheme,
      sizeCode: selectedSize,
      isOOE: !!isOOE,
      sizeName: getSizeLabel(selectedSize, isOOE ? null : (selectedVariant.SizeInfo?.SizeDescription || selectedSize)),
      analyticsSizeName: prodSizeName(selectedSize, selectedVariant.SizeInfo?.SizeDescription, selectedProduct.ProductName),
      price: displayPrice,
      estimatedDelivery: "5-7",
      plateImages: {
        isOOE,
        single: isOOE
          ? null
          : generatedPlateImage
          ? getPlateImageDataUrl(generatedPlateImage)
          : null,
        standard:
          isOOE && plateSTImage ? getPlateImageDataUrl(plateSTImage) : null,
        slimline:
          isOOE && plateSLImage ? getPlateImageDataUrl(plateSLImage) : null,
      },
      timestamp: new Date().toISOString(),
    };

    // GA: add_to_cart — only after the availability re-check passed (prod: added to cart).
    trackAddToCart(plateItemFromOrder(orderData, PRODUCT_TYPE.CREATE));

    sessionStorage.setItem("plateOrderData", JSON.stringify(orderData));
    // When the modal history entry is present, use replace so the
    // navigation replaces the pushed modal entry instead of stacking
    // on top of it (avoids a duplicate styles-page in the back stack).
    const hasModalEntry = modalHistoryPushed.current;
    modalHistoryPushed.current = false;
    navigate('/create/personal-details', hasModalEntry ? { replace: true } : undefined);
  };

  const handleLoadMore = () => {
    setDisplayCount((prev) => prev + 12);
  };

  const resetFilter = () => {
    setFilterCategory([]);
    setFilterPrice([]);
    setFilterColour([]);
    setFilterSize([]);
    setSortCriteria("popular");
  };

  const formatPrice = (price) => {
    if (!price) return "0";
    return price;
  };

  const getGroupedColourSchemes = () => {
    if (!allVariants.length) return [];

    const uniqueColors = [];
    const seenColors = new Set();

    const isThemeBased = allVariants.some(v => v.ThemeInfo?.Theme);
    const isLucky = selectedProduct?.ProductName?.toLowerCase().includes('lucky');
    const isMultiColourTheme = isThemeBased && !isLucky && (() => {
      const themeColours = {};
      for (const v of allVariants) {
        const theme = v.ThemeInfo?.Theme || '';
        const colour = v.ColourInfo?.DisplayName || v.ColourInfo?.Colour || '';
        if (!themeColours[theme]) themeColours[theme] = new Set();
        if (colour) themeColours[theme].add(colour);
      }
      return Object.values(themeColours).some(colours => colours.size > 1);
    })();
    for (const variant of allVariants) {
      if (variant.ColourInfo) {
        const colorKey = isThemeBased && !isLucky && !isMultiColourTheme
          ? '_theme_'
          : `${variant.ColourInfo.DisplayName || ''}_${variant.ColourInfo.ColourSelectorHexOuter}_${variant.ColourInfo.ColourSelectorHexFont}`;
        if (!seenColors.has(colorKey)) {
          seenColors.add(colorKey);

          const foreground = normalizeHex(
            variant.ColourInfo.ColourSelectorHexFont
          );
          const background = normalizeHex(
            variant.ColourInfo.ColourSelectorHexOuter
          );

          uniqueColors.push({
            key: colorKey,
            Foreground: foreground,
            Background: background,
            borderColour: foreground,
            name: variant.ColourInfo.DisplayName || variant.ColourInfo.Colour || "Unknown",
            colour: variant.ColourInfo.Colour,
            variantName: variant.VariantName,
          });
        }
      }
    }
    return uniqueColors.sort((a, b) => a.name.localeCompare(b.name));
  };

  const getVariantsForColourScheme = (cs) => {
    if (!cs) return [];
    if (cs.key === '_theme_') {
      return allVariants.filter(v => v.ColourInfo);
    }
    return allVariants.filter(v =>
      v.ColourInfo?.Colour === cs.colour ||
      (cs.name && v.ColourInfo?.DisplayName === cs.name)
    );
  };

  const getAvailableThemes = () => {
    if (!allVariants.length || !selectedColourScheme) return [];

    const selectedColorScheme = getGroupedColourSchemes().find(
      (c) => c.key === selectedColourScheme
    );
    if (!selectedColorScheme) return [];

    const matchingVariants = getVariantsForColourScheme(selectedColorScheme);

    const themes = matchingVariants
      .map((v) => v.ThemeInfo?.Theme)
      .filter(Boolean);
    return [...new Set(themes)];
  };

  const getAvailableSizes = () => {
    if (!allVariants.length || !selectedColourScheme) return [];

    const selectedColorScheme = getGroupedColourSchemes().find(
      (c) => c.key === selectedColourScheme
    );
    if (!selectedColorScheme) return [];

    const matchingVariants = getVariantsForColourScheme(selectedColorScheme);

    const nonOOEVariants = matchingVariants.filter((v) => !v.IsOOE);
    const sizes = nonOOEVariants
      .map((v) => ({
        code: v.SizeInfo?.SizeCode,
        description: v.SizeInfo?.SizeDescription,
      }))
      .filter((s) => s.code);

    const uniqueSizes = [...new Map(sizes.map((s) => [s.code, s])).values()];

    // Only add OOE option when a real OOE variant (IsOOE === true) exists in the API response
    const hasRealOOE = matchingVariants.some((v) => v.IsOOE === true);
    if (hasRealOOE && !uniqueSizes.some((s) => s.code === "OOE")) {
      const stSize = uniqueSizes.find((s) => s.code === "ST");
      const slSize = uniqueSizes.find((s) => s.code === "SL");
      const ooeDescription = [stSize?.description, slSize?.description]
        .filter(Boolean)
        .join(" / ");
      uniqueSizes.push({ code: "OOE", description: ooeDescription });
    }

    // Display order: OOE first, then ST, then SL, then any others
    const sizeOrder = { OOE: 0, ST: 1, SL: 2 };
    uniqueSizes.sort(
      (a, b) => (sizeOrder[a.code] ?? 99) - (sizeOrder[b.code] ?? 99)
    );

    return uniqueSizes;
  };

  const getAvailableThemesForColor = () => {
    if (!allVariants.length || !selectedColourScheme) return [];

    const selectedColorScheme = getGroupedColourSchemes().find(
      (c) => c.key === selectedColourScheme
    );
    if (!selectedColorScheme) return [];

    const matchingVariants = getVariantsForColourScheme(selectedColorScheme);

    const themes = matchingVariants
      .map((v) => v.ThemeInfo?.Theme)
      .filter(Boolean);
    return [...new Set(themes)];
  };

  const getSelectedVariant = () => {
    if (!allVariants.length || !selectedColourScheme) return null;

    const selectedColorScheme = getGroupedColourSchemes().find(
      (c) => c.key === selectedColourScheme
    );
    if (!selectedColorScheme) {
      return null;
    }

    const isOOE = selectedSize === "OOE";
    return getVariantsForColourScheme(selectedColorScheme).find(
      (v) =>
        (!selectedTheme || v.ThemeInfo?.Theme === selectedTheme) &&
        (isOOE
          ? v.IsOOE === true
          : !v.IsOOE && (!selectedSize || v.SizeInfo?.SizeCode === selectedSize))
    );
  };

  // Auto-update theme and size when color changes
  useEffect(() => {
    if (!selectedColourScheme || !allVariants.length) return;

    const selectedColorScheme = getGroupedColourSchemes().find(
      (c) => c.key === selectedColourScheme
    );
    if (!selectedColorScheme) {
      return;
    }

    const matchingVariants = getVariantsForColourScheme(selectedColorScheme);

    if (matchingVariants.length === 0) return;

    const availableThemes = matchingVariants
      .map((v) => v.ThemeInfo?.Theme)
      .filter(Boolean);
    const uniqueThemes = [...new Set(availableThemes)];

    const nonOOEVariants = matchingVariants.filter((v) => !v.IsOOE);
    const availableSizes = nonOOEVariants
      .map((v) => v.SizeInfo?.SizeCode)
      .filter(Boolean);
    const uniqueSizes = [...new Set(availableSizes)];
    const hasRealOOE = matchingVariants.some((v) => v.IsOOE === true);
    const validSizes = hasRealOOE ? [...uniqueSizes, "OOE"] : uniqueSizes;

    if (uniqueThemes.length > 0) {
      if (!selectedTheme || !uniqueThemes.includes(selectedTheme)) {
        setSelectedTheme(uniqueThemes[0]);
      }
    } else {
      setSelectedTheme("");
    }

    if (validSizes.length > 0) {
      if (!selectedSize || !validSizes.includes(selectedSize)) {
        const defaultSize = hasRealOOE ? "OOE" : validSizes[0];
        setSelectedSize(defaultSize);
      }
    } else {
      setSelectedSize(null);
    }
  }, [selectedColourScheme, allVariants]);

  const getVariantBySize = (sizeCode) => {
    if (!allVariants.length || !selectedColourScheme) return null;

    const selectedColorScheme = getGroupedColourSchemes().find(
      (c) => c.key === selectedColourScheme
    );
    if (!selectedColorScheme) return null;

    if (sizeCode === "OOE") {
      return getVariantsForColourScheme(selectedColorScheme).find(
        (v) =>
          (!selectedTheme || v.ThemeInfo?.Theme === selectedTheme) &&
          v.IsOOE === true
      );
    }
    return getVariantsForColourScheme(selectedColorScheme).find(
      (v) =>
        (!selectedTheme || v.ThemeInfo?.Theme === selectedTheme) &&
        !v.IsOOE &&
        v.SizeInfo?.SizeCode === sizeCode
    );
  };

  // Generate plate image when variant selection changes
  useEffect(() => {
    const generatePlateImage = async () => {
      if (!combination || !selectedColourScheme) {
        setGeneratedPlateImage(null);
        setPlateSTImage(null);
        setPlateSLImage(null);
        return;
      }

      const isOOE =
        selectedSize &&
        (selectedSize.toLowerCase() === "ooe" || selectedSize === "OOE");

      if (isOOE) {
        setIsGeneratingImage(true);

        try {
          const variantST = getVariantBySize("ST");
          const variantSL = getVariantBySize("SL");

          if (!variantST || !variantSL) {
            setPlateSTImage(null);
            setPlateSLImage(null);
            setIsGeneratingImage(false);
            return;
          }

          const inputST = preparePlateImageInput(variantST, combination);

          const inputSL = preparePlateImageInput(variantSL, combination);

          const [resultST, resultSL] = await Promise.all([
            executeMutation(plateImageGenerator, { input: inputST }),
            executeMutation(plateImageGenerator, { input: inputSL }),
          ]);

          if (resultST?.generatePlateImage) {
            setPlateSTImage(resultST.generatePlateImage);
          }
          if (resultSL?.generatePlateImage) {
            setPlateSLImage(resultSL.generatePlateImage);
          }
          setGeneratedPlateImage(null);
        } catch (error) {
          setPlateSTImage(null);
          setPlateSLImage(null);
        } finally {
          setIsGeneratingImage(false);
        }
      } else {
        const selectedVariant = getSelectedVariant();

        if (!selectedVariant) {
          setGeneratedPlateImage(null);
          setPlateSTImage(null);
          setPlateSLImage(null);
          return;
        }

        setIsGeneratingImage(true);
        try {
          const input = preparePlateImageInput(selectedVariant, combination);

          if (input) {
            const result = await executeMutation(plateImageGenerator, {
              input,
            });
            if (result?.generatePlateImage) {
              setGeneratedPlateImage(result.generatePlateImage);
              setPlateSTImage(null);
              setPlateSLImage(null);
            }
          }
        } catch (error) {
          setGeneratedPlateImage(null);
        } finally {
          setIsGeneratingImage(false);
        }
      }
    };

    generatePlateImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColourScheme, selectedTheme, selectedSize, combination]);

  useEffect(() => {
    if (!selectedProduct || !dialogRef.current || !endCtaRef.current) {
      setIsEndCtaVisible(false);
      return;
    }
    // eslint-disable-next-line no-undef
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsEndCtaVisible(entry.isIntersecting);
      },
      { root: dialogRef.current, threshold: 0 }
    );
    observer.observe(endCtaRef.current);
    return () => observer.disconnect();
  }, [selectedProduct]);

  const colourSchemes = getGroupedColourSchemes();
  const availableThemes = getAvailableThemesForColor();
  const availableSizes = getAvailableSizes();
  const selectedVariant = getSelectedVariant();

  const displayPrice = selectedVariant?.CostInfoForCreate?.Cost || selectedProduct?.ListPrice;

  return (
    <div id="content">
      {/* @starting-style not supported by older transpilers — keep as inline style tag */}
      <style>{`
        @media (min-width: 660px) {
          .modal-selection {
            transition: translate 0.5s ease-in-out, overlay 0.5s allow-discrete, display 0.5s allow-discrete;
            translate: 100% 0;
          }
          .modal-selection[open] { translate: 0 0; }
          @starting-style {
            .modal-selection[open] { translate: 100% 0; }
          }
        }
        /* VP-3831: remove the double horizontal padding on the slide-in
           combo-check modal (unavailable-style branch where
           .modal-product-combo__body is nested inside .modal-selection__body
           and both add 24px). Only applies when that inner wrapper is
           present, so available-style modals keep their normal 24px. */
        .modal-selection .modal-selection__body:has(.modal-product-combo__body) {
          padding-left: 0;
          padding-right: 0;
        }
      `}</style>

      <div className="row">
        <div className="page-header page-header--small">
          <div className="page-header__content">
            <h1 className="page-header__title">{c.pageTitle || 'Create a plate'}</h1>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="plate-wizard">
          <Wizard
            steps={wizardSteps}
            currentStep={1}
            backLink={comboBackHref}
          />

          <div className={`plate-wizard__form plate-wizard__form--style ${styles.formNoBottomPad}`}>
            <div className="plate-wizard__form-header">
              <h2 className="plate-wizard__form-title" tabIndex="-1">
                {c.sectionTitle || 'Select a style'}
              </h2>
              <div>
                <div
                  role="region"
                  aria-live="polite"
                  class="style-selector__details"
                >
                  <dl class="style-selector__details-item style-selector__details-item--combo">
                    <dt class="style-selector__details-item-title">
                      {c.combinationLabel || 'Your plate combination:'}
                    </dt>
                    <dd class="style-selector__details-item-value">
                      {combination}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>

            <form
              className={`filtered-products${loading ? " loading" : ""}`}
              aria-label="Product list"
            >
              {loading ? (
                <div className="products products--ready">
                  <div className="products__inner">
                    <ProductGridSkeleton disclaimers={c.disclaimers} />
                  </div>
                </div>
              ) : (
                <>
                  <ProductFilter
                    categories={allCategories}
                    priceRanges={allPriceRanges}
                    colours={allColours}
                    sizes={allSizes}
                    filterCategory={filterCategory}
                    filterPrice={filterPrice}
                    filterColour={filterColour}
                    filterSize={filterSize}
                    sortCriteria={sortCriteria}
                    onFilterChange={(type, value) => {
                      if (type === "category") setFilterCategory(value);
                      else if (type === "price") setFilterPrice(value);
                      else if (type === "colour") setFilterColour(value);
                      else if (type === "size") setFilterSize(value);
                    }}
                    filteredCount={activeFilterCount}
                    onSortChange={setSortCriteria}
                    onReset={resetFilter}
                  />

                  <output className="filtered-products__output">
                    <div className="products products--ready">
                      <div className="products__inner">
                        <ProductList
                          products={sortedAvailableProducts}
                          selectedProduct={selectedProduct}
                          onProductClick={handleProductClick}
                          title={c.availableStylesTitle || 'Available styles'}
                          loading={false}
                          formatPrice={formatPrice}
                          unavailable={false}
                          productImages={productImages}
                          totalCount={new Set(sortedAvailableProducts.map(c => c._originalProductId)).size}
                        />

                        {displayedUnavailableProducts.length > 0 && (
                          <ProductList
                            products={sortedUnavailableProducts}
                            onProductClick={handleProductClick}
                            displayCount={displayCount}
                            onLoadMore={handleLoadMore}
                            title={c.unavailableStylesTitle || 'Unavailable styles'}
                            loading={false}
                            formatPrice={formatPrice}
                            unavailable={true}
                            productImages={productImages}
                            totalCount={new Set(sortedUnavailableProducts.map(c => c._originalProductId)).size}
                          />
                        )}
                        <div className="styles__disclaimer">
                          {(c.disclaimers || [
                            'Plate images are a guide only. There may be small variations to colour and spacing.',
                            'Representations of plates provided on this page are for illustration purposes only, final products may vary slightly from what is displayed on-screen.',
                          ]).map((text, i) => <p key={i}>{text}</p>)}
                        </div>
                      </div>
                    </div>
                  </output>
                </>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Style selection modal — slides in from right */}
      <dialog
        ref={dialogRef}
        className="modal-selection"
        onClose={handleDialogClose}
        onClick={(e) => {
          if (e.target === dialogRef.current) closeDialog();
        }}
      >
        {selectedProduct && (
          <div className="modal-selection__inner">
            {/* Covers backdrop area; click to close */}
            <div
              className="modal-selection__inner-back"
              onClick={closeDialog}
            />
            <div className="modal-selection__content">
              <div className="modal-selection__header">
                <button
                  type="button"
                  className="modal-selection__close"
                  onClick={closeDialog}
                >
                  <span className="visuallyhidden">Close</span>
                </button>
                <h3 className="modal-selection__title" tabIndex={-1}>
                  <span className="visuallyhidden">
                    Select your style details:{" "}
                  </span>
                  {selectedProduct.ProductName}
                </h3>
              </div>

              {selectedProduct.IsAvailable !== true ? (
                <div className="modal-selection__body">
                  {unavailableBuyNowModal.enabled && unavailableBuyNowModal.products ? (
                    (() => {
                      /* VP-3831: show only the clicked product, not both Euro / Euro
                         Premium together. Keeps PROD's personalised flow. */
                      const matched = unavailableBuyNowModal.products.filter(
                        p => p.productName === selectedProduct.ProductName
                      );
                      const modalProducts = matched.length > 0 ? matched : unavailableBuyNowModal.products;
                      return (
                        <BuyNowComboContent
                          key={selectedProduct.ProductName}
                          modalProducts={modalProducts}
                          initialProductIdx={0}
                          productTitle={selectedProduct.ProductName}
                          allProducts={products}
                          c={comboLabels}
                          onApplyAndContinue={(fullCombo, selProd) => {
                            const vt = (selProd?.vehicleType ?? 1) === 1 ? 'car' : 'motorcycle';
                            const colourParam = selProd?.previewColourDisplayName || '';
                            // Replace the modal's history entry instead of letting handleDialogClose call
                            // history.back(): that traversal is async and would land after this navigate,
                            // reverting the URL to the previous combination.
                            const hasModalEntry = modalHistoryPushed.current;
                            modalHistoryPushed.current = false;
                            closeDialog();
                            // Same pathname, so ScrollToTop won't fire — start the new combo at the top (as prod).
                            window.scrollTo(0, 0);
                            navigate(`/create/select-a-style?combination=${encodeURIComponent(fullCombo)}&vehicleType=${vt}&productName=${encodeURIComponent(selProd?.productName || '')}${colourParam ? `&colour=${encodeURIComponent(colourParam)}` : ''}`, hasModalEntry ? { replace: true } : undefined);
                          }}
                          onClose={closeDialog}
                        />
                      );
                    })()
                  ) : (
                    <div className="unavailable-style">
                      <p className="unavailable-style__intro">
                        {c.unavailableStyleTitle || 'This style is unavailable for your selected combination'}
                      </p>
                      <img
                        className="unavailable-style__image"
                        src={
                          productImages[selectedProduct.ProductId] ||
                          selectedProduct.SampleImageId ||
                          "/images/placeholder-plate.png"
                        }
                        alt={`Example image of a ${selectedProduct.ProductName} plate`}
                      />
                      <h4 className="unavailable-style__subtitle">
                        Choosing a {selectedProduct.ProductName} combination
                      </h4>
                      <div className="unavailable-style__text">
                        {unavailableSpecs.combinationOptionsDescription
                          ? <div dangerouslySetInnerHTML={{ __html: unavailableSpecs.combinationOptionsDescription }} />
                          : <p>{c.unavailableStyleBody || 'This style may be available with a different plate combination. Please try selecting a different combination to see if this style becomes available.'}</p>
                        }
                      </div>
                      {unavailableSpecs.combinationOptions && (
                        <div className="unavailable-style__combo-options">
                          <p><strong>{selectedProduct.ProductName} {c.combinationOptionsLabel || 'combination options'}</strong></p>
                          <div dangerouslySetInnerHTML={{ __html: unavailableSpecs.combinationOptions }} />
                        </div>
                      )}
                      <div className="unavailable-style__actions">
                        <Link
                          className="unavailable-style__action unavailable-style__action--combo"
                          to={`/?combination=${combination}&vehicleType=${vehicleType}`}
                        >
                          {c.selectNewCombinationText || 'Select new combination'}
                        </Link>
                        <Link
                          className="unavailable-style__action unavailable-style__action--details"
                          to={`/browse-styles/${selectedProduct.ProductName.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {c.seeMoreProductDetailsText || 'See more product details'}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div
                    className={`modal-selection__body${
                      loadingVariants ? " loading" : ""
                    }`}
                  >
                    {loadingVariants ? (
                      <LoadingPanel
                        loading={true}
                        message={c.loadingVariantsText || 'Loading style options...'}
                      />
                    ) : (
                      <>
                        {(() => {
                          const isAcrylic = selectedProduct?.ProductName?.toLowerCase().includes('deluxe');
                          return (
                            <details className="modal-selection__info">
                              <summary className="modal-selection__info-title">
                                {isAcrylic ? (c.goodToKnowAcrylicTitle || 'Good to know') : (c.goodToKnowTitle || 'Good to know')}
                              </summary>
                              <div className="modal-selection__info-content">
                                {isAcrylic
                                  ? <span dangerouslySetInnerHTML={{ __html: c.goodToKnowAcrylicHtml || '<p>Deluxe plates are made from premium acrylic material. Plate images are a guide only. There may be small variations to colour and spacing.</p>' }} />
                                  : <p>{c.goodToKnowText || 'Plate images are a guide only. There may be small variations to colour and spacing.'}</p>
                                }
                              </div>
                            </details>
                          );
                        })()}

                        <form
                          className="modal-style-form"
                          id="style-selection"
                          onSubmit={handleGetThisPlate}
                        >
                          {/* Plate preview */}
                          {selectedColourScheme && (
                            <div className="modal-style-form__preview">
                              <h4 className="visuallyhidden">
                                Your selected plates
                              </h4>
                              <dl
                                className={`modal-style-form__preview-list${
                                  isGeneratingImage ? " loading" : ""
                                }`}
                              >
                                {isGeneratingImage ? (
                                  <LoadingPanel
                                    loading={true}
                                    message={c.generatingPreviewText || 'Generating preview...'}
                                  />
                                ) : plateSTImage && plateSLImage && vehicleType === 'car' ? (
                                  <>
                                    <div className="modal-style-form__preview-list-item">
                                      <dt className="modal-style-form__preview-list-size">
                                        <b>{c.standardPlatePreviewLabel || '1 Standard'}</b>{(() => { const d = formatDimensions(getVariantBySize('ST')); return d ? ` — ${d}` : ''; })()}
                                      </dt>
                                      <dd className="modal-style-form__preview-list-img-container">
                                        <img
                                          className="modal-style-form__preview-list-img"
                                          src={getPlateImageDataUrl(plateSTImage)}
                                          alt="Standard plate preview"
                                          width="520"
                                          height="140"
                                        />
                                      </dd>
                                    </div>
                                    <div className="modal-style-form__preview-list-item">
                                      <dt className="modal-style-form__preview-list-size">
                                        <b>{c.slimlinePlatePreviewLabel || '1 Slimline'}</b>{(() => { const d = formatDimensions(getVariantBySize('SL')); return d ? ` — ${d}` : ''; })()}
                                      </dt>
                                      <dd className="modal-style-form__preview-list-img-container">
                                        <img
                                          className="modal-style-form__preview-list-img"
                                          src={getPlateImageDataUrl(plateSLImage)}
                                          alt="Slimline plate preview"
                                          width="520"
                                          height="140"
                                        />
                                      </dd>
                                    </div>
                                  </>
                                ) : plateSTImage && vehicleType !== 'car' ? (
                                  <div className="modal-style-form__preview-list-item">
                                    <dd className="modal-style-form__preview-list-img-container">
                                      <img
                                        className="modal-style-form__preview-list-img"
                                        src={getPlateImageDataUrl(plateSTImage)}
                                        alt="Plate preview"
                                        width="520"
                                        height="140"
                                      />
                                    </dd>
                                  </div>
                                ) : generatedPlateImage && (selectedSize === 'ST' || selectedSize === 'SL') ? (
                                  vehicleType === 'car' ? (
                                    <>
                                      <div className="modal-style-form__preview-list-item">
                                        <dt className="modal-style-form__preview-list-size">
                                          <b>{selectedSize === 'ST' ? (c.standardSizeLabel || '2 Standard') : (c.slimlineSizeLabel || '2 Slimline')}</b>
                                          {(() => { const d = formatDimensions(selectedVariant); return d ? ` — ${d}` : ''; })()}
                                        </dt>
                                      </div>
                                      <div className="modal-style-form__preview-list-item">
                                        <dd className="modal-style-form__preview-list-img-container">
                                          <img
                                            className="modal-style-form__preview-list-img"
                                            src={getPlateImageDataUrl(generatedPlateImage)}
                                            alt={`${selectedSize === 'ST' ? 'Standard' : 'Slimline'} plate preview`}
                                            width="520"
                                            height="140"
                                          />
                                        </dd>
                                      </div>
                                      <div className="modal-style-form__preview-list-item">
                                        <dd className="modal-style-form__preview-list-img-container">
                                          <img
                                            className="modal-style-form__preview-list-img"
                                            src={getPlateImageDataUrl(generatedPlateImage)}
                                            alt={`${selectedSize === 'ST' ? 'Standard' : 'Slimline'} plate preview`}
                                            width="520"
                                            height="140"
                                          />
                                        </dd>
                                      </div>
                                    </>
                                  ) : (
                                    <div className="modal-style-form__preview-list-item">
                                      <dt className="modal-style-form__preview-list-size">
                                        <b>{getSizeLabel(selectedSize, selectedSize)}</b>{formatDimensions(selectedVariant) ? ` — ${formatDimensions(selectedVariant)}` : ''}
                                      </dt>
                                      <dd className="modal-style-form__preview-list-img-container">
                                        <img
                                          className="modal-style-form__preview-list-img"
                                          src={getPlateImageDataUrl(generatedPlateImage)}
                                          alt={`${selectedSize === 'ST' ? 'Standard' : 'Slimline'} plate preview`}
                                          width="520"
                                          height="140"
                                        />
                                      </dd>
                                    </div>
                                  )
                                ) : (
                                  <div className="modal-style-form__preview-list-item">
                                    <dd className="modal-style-form__preview-list-img-container">
                                      <img
                                        className="modal-style-form__preview-list-img"
                                        src={
                                          generatedPlateImage
                                            ? getPlateImageDataUrl(generatedPlateImage)
                                            : selectedProduct.SampleImageId ||
                                              "/images/placeholder-plate.png"
                                        }
                                        alt="Preview image of your plate"
                                        width="520"
                                        height="140"
                                      />
                                    </dd>
                                  </div>
                                )}
                              </dl>
                            </div>
                          )}

                          {/* Colour */}
                          {colourSchemes.length > 0 && (
                            <fieldset className="modal-style-form__field">
                              <legend
                                className={`modal-style-form__label${
                                  colourSchemes.length > 1
                                    ? " modal-style-form__label--with-value-display"
                                    : ""
                                }`}
                                id="colour-fieldset-label"
                              >
                                {c.colourLabel || 'Colour'}
                              </legend>
                              <label
                                htmlFor="selected-colour-text"
                                className="visuallyhidden"
                              >
                                Description of selected colour
                              </label>
                              <input
                                className={`modal-style-form__field-value-display${
                                  colourSchemes.length === 1
                                    ? " modal-style-form__field-value-display--single-value"
                                    : ""
                                }`}
                                id="selected-colour-text"
                                type="text"
                                readOnly
                                value={
                                  colourSchemes.find(
                                    (c) => c.key === selectedColourScheme
                                  )?.name || ""
                                }
                              />
                              {colourSchemes.length > 1 && (
                                <div className="modal-style-form__colours">
                                  {colourSchemes.map((colourScheme) => (
                                    <div
                                      key={colourScheme.key}
                                      className="modal-style-form__colour"
                                    >
                                      <input
                                        type="radio"
                                        id={`colour-${colourScheme.key}`}
                                        name="modal-style-form-colour"
                                        value={colourScheme.key}
                                        checked={
                                          selectedColourScheme ===
                                          colourScheme.key
                                        }
                                        onChange={(e) =>
                                          setSelectedColourScheme(
                                            e.target.value
                                          )
                                        }
                                        className="visuallyhidden visuallyhidden--no-focus"
                                        aria-describedby="colour-fieldset-label"
                                      />
                                      <label
                                        className={`modal-style-form__colour-label${
                                          selectedColourScheme ===
                                          colourScheme.key
                                            ? " modal-style-form__colour-label--selected"
                                            : ""
                                        }${
                                          colourScheme.Background.toLowerCase() ===
                                          "ffffff"
                                            ? " modal-style-form__colour-label--with-border"
                                            : ""
                                        }`}
                                        htmlFor={`colour-${colourScheme.key}`}
                                        style={{
                                          color: `#${colourScheme.Foreground}`,
                                          backgroundColor: `#${colourScheme.Background}`,
                                        }}
                                      >
                                        <span className="visuallyhidden">
                                          {colourScheme.name}
                                        </span>
                                        <span aria-hidden="true">
                                          {selectedColourScheme !==
                                          colourScheme.key ? (
                                            <span>A</span>
                                          ) : (
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              className="modal-style-form__colour-svg"
                                              width="18"
                                              height="13"
                                              fill="none"
                                              viewBox="0 0 18 13"
                                            >
                                              <path
                                                stroke={`#${colourScheme.Foreground}`}
                                                strokeWidth="3.222"
                                                d="m1.139 4.812 5.235 5.235 9.665-8.86"
                                              />
                                            </svg>
                                          )}
                                        </span>
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </fieldset>
                          )}

                          {/* Theme */}
                          {availableThemes.length === 1 && (
                            <dl className="modal-style-form__field modal-style-form__field--static-list">
                              <dt className="modal-style-form__label">{c.themeLabel || 'Theme'}</dt>
                              <dd className="modal-style-form__static-value">
                                {availableThemes[0]}
                              </dd>
                            </dl>
                          )}
                          {availableThemes.length > 1 && (
                            <fieldset className="modal-style-form__field modal-style-form__field--radio">
                              <legend
                                className="modal-style-form__label"
                                id="theme-fieldset-label"
                              >
                                {c.themeLabel || 'Theme'}
                              </legend>
                              {availableThemes.map((theme) => (
                                <div
                                  key={theme}
                                  className="modal-style-form__radio-item"
                                >
                                  <input
                                    type="radio"
                                    id={`theme-${theme}`}
                                    name="modal-style-form-theme"
                                    value={theme}
                                    checked={selectedTheme === theme}
                                    onChange={(e) =>
                                      setSelectedTheme(e.target.value)
                                    }
                                    className="visuallyhidden visuallyhidden--no-focus"
                                    aria-describedby="theme-fieldset-label"
                                  />
                                  <label
                                    htmlFor={`theme-${theme}`}
                                    className="modal-style-form__radio-item-label"
                                  >
                                    <span className="modal-style-form__radio-item-label-text">
                                      {theme}
                                    </span>
                                  </label>
                                </div>
                              ))}
                            </fieldset>
                          )}

                          {/* Size */}
                          {availableSizes.length === 1 && (
                            <dl className="modal-style-form__field modal-style-form__field--static-list">
                              <dt className="modal-style-form__label">
                                {c.plateSizeLabel || 'Plates sizes'}
                              </dt>
                              <dd className="modal-style-form__static-value">
                                {getSizeLabel(availableSizes[0].code, availableSizes[0].description)}
                                {(() => { const d = formatDimensions(getVariantBySize(availableSizes[0].code)); return d ? ` ${d}` : ''; })()}
                              </dd>
                            </dl>
                          )}
                          {availableSizes.length > 1 && (
                            <fieldset className="modal-style-form__field modal-style-form__field--radio modal-style-form__field--horizontal-desktop">
                              <legend
                                className="modal-style-form__label"
                                id="size-fieldset-label"
                              >
                                {c.plateSizeLabel || 'Plates sizes'}
                              </legend>
                              {availableSizes.map((size) => (
                                <div
                                  key={size.code}
                                  className="modal-style-form__radio-item"
                                >
                                  <input
                                    type="radio"
                                    id={`size-${size.code}`}
                                    name="modal-style-form-size"
                                    value={size.code}
                                    checked={selectedSize === size.code}
                                    onChange={(e) =>
                                      setSelectedSize(e.target.value)
                                    }
                                    className="visuallyhidden visuallyhidden--no-focus"
                                    aria-describedby="size-fieldset-label"
                                  />
                                  <label
                                    htmlFor={`size-${size.code}`}
                                    className="modal-style-form__radio-item-label"
                                  >
                                    <span className="modal-style-form__radio-item-label-text">
                                      <span className="modal-style-form__radio-item-label-primary">
                                        {getSizeLabel(size.code, size.description)}
                                      </span>
                                      {vehicleType === 'car' && (() => {
                                        let dims;
                                        if (size.code === 'OOE') {
                                          const dST = formatDimensions(getVariantBySize('ST'));
                                          const dSL = formatDimensions(getVariantBySize('SL'));
                                          dims = dST && dSL ? `${dST} / ${dSL}` : (dST || dSL);
                                        } else {
                                          dims = formatDimensions(getVariantBySize(size.code));
                                        }
                                        return dims ? (
                                          <>
                                            <span className="visuallyhidden">
                                              {" "}
                                              -{" "}
                                            </span>
                                            <span className="modal-style-form__radio-item-label-secondary">
                                              {dims}
                                            </span>
                                          </>
                                        ) : null;
                                      })()}
                                    </span>
                                  </label>
                                </div>
                              ))}
                            </fieldset>
                          )}

                          {/* Slogans */}
                          {(() => {
                            const isOOE =
                              selectedSize &&
                              selectedSize.toLowerCase() === "ooe";
                            if (isOOE) {
                              const sloganST = getVariantBySize("ST")?.SizeInfo?.SloganDesc?.SloganDescription;
                              const sloganSL = getVariantBySize("SL")?.SizeInfo?.SloganDesc?.SloganDescription;
                              if (!sloganST && !sloganSL) return null;
                              const sameSlogan = sloganST && sloganSL && sloganST === sloganSL;
                              return (
                                <dl className="modal-style-form__field modal-style-form__field--static-list">
                                  <dt className="modal-style-form__label">
                                    {sameSlogan ? (c.sloganLabel || 'Slogan') : (c.slogansLabel || 'Slogans')}
                                  </dt>
                                  {sameSlogan ? (
                                    <dd className="modal-style-form__static-value" dangerouslySetInnerHTML={{ __html: sloganST.replace(/\|/g, "<br />") }} />
                                  ) : (
                                    <>
                                      {sloganST && <dd className="modal-style-form__static-value" dangerouslySetInnerHTML={{ __html: sloganST.replace(/\|/g, "<br />") }} />}
                                      {sloganSL && <dd className="modal-style-form__static-value" dangerouslySetInnerHTML={{ __html: sloganSL.replace(/\|/g, "<br />") }} />}
                                    </>
                                  )}
                                </dl>
                              );
                            }
                            const slogan =
                              selectedVariant?.SizeInfo?.SloganDesc
                                ?.SloganDescription;
                            return slogan ? (
                              <dl className="modal-style-form__field modal-style-form__field--static-list">
                                <dt className="modal-style-form__label">
                                  {c.sloganLabel || 'Slogan'}
                                </dt>
                                <dd
                                  className="modal-style-form__static-value"
                                  dangerouslySetInnerHTML={{
                                    __html: slogan.replace(/\|/g, "<br />"),
                                  }}
                                />
                              </dl>
                            ) : null;
                          })()}

                          <input
                            type="hidden"
                            name="style-variant-id"
                            value={selectedVariant?.VariantId || ""}
                          />
                        </form>
                      </>
                    )}
                  </div>

                  <div className="modal-selection__footer modal-selection__footer--static">
                    {selectedVariant && (
                      <p className="modal-selection__footer-static-text">
                        Price: ${parseFloat(displayPrice)} AUD
                      </p>
                    )}
                    {availabilityError && (
                      <div className="input__error-container" style={{ marginBottom: "16px" }}>
                        <div className="input__error"><p>{availabilityError}</p></div>
                      </div>
                    )}
                    <p className="modal-selection__footer-disclaimer">
                      <strong>Delivery:</strong> {c.deliveryText || 'FREE - Plates are delivered to your nominated VicRoads postal address'}
                    </p>
                    <button
                      ref={endCtaRef}
                      className="modal-selection__footer-cta"
                      form="style-selection"
                      disabled={
                        !selectedColourScheme ||
                        loadingVariants ||
                        checkingAvailability
                      }
                    >
                      {c.getThisPlateText || 'Get this plate'}
                    </button>
                  </div>

                  <div className={`modal-selection__footer${isEndCtaVisible ? ' modal-selection__footer--hidden' : ''}`}>
                    <output
                      form="style-selection"
                      className="modal-selection__footer-summary"
                    >
                      {selectedVariant && (
                        <p className="modal-selection__footer-summary-text">
                          <span className="visuallyhidden">
                            Your order for{" "}
                          </span>
                          <span className="modal-selection__footer-size">
                            {getSizeLabel(selectedSize, selectedVariant.SizeInfo?.SizeDescription || selectedSize)}
                          </span>
                          <span className="visuallyhidden">
                            {" "}will cost{" "}
                          </span>
                          <span className="modal-selection__footer-price">
                            ${parseFloat(displayPrice)} AUD
                          </span>
                        </p>
                      )}
                    </output>
                    <button
                      className="modal-selection__footer-cta"
                      form="style-selection"
                      disabled={
                        !selectedColourScheme ||
                        loadingVariants ||
                        checkingAvailability
                      }
                    >
                      {c.getThisPlateText || 'Get this plate'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </dialog>
    </div>
  );
};

// Remount when the query changes (e.g. "Apply and continue" from the unavailable-style
// panel navigates to this route with a new combination) so all state is rebuilt from
// the new URL — combination, session, and the productName restore that reopens the panel.
const CreateStylesPageRoute = () => {
  const { search } = useLocation();
  return <CreateStylesPage key={search} />;
};

export default CreateStylesPageRoute;
